/* eslint-disable complexity */
import { skipToken } from '@reduxjs/toolkit/query';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { ActionButtonPageTitle } from '@strata/react-buttons';
import { ButtonWithDropdownMenu } from '@strata/react-dropdown-menu';
import { FormHeaderAndTabbedContent } from '@strata/react-form-header-and-content';
import { InlineMessage } from '@strata/react-inline-message';
import { FormTitleLabelValuePair, HeaderLabelValuePair } from '@strata/react-label-value-pairs';
import { ServicingRate } from '@strata/react-servicing-rate';
import { FillProgressIcon } from '@strata/react-status-icons';
import {
    OpportunityStatusIndicator,
    ReferralStatusIndicator,
} from '@strata/react-status-indicators';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { ClinicalPolicyDialog } from 'components/referrals/dialogs/ClinicalPolicyDialog';
import { PerformanceProgress } from 'components/referrals/PerformanceProgress';
import { ReferralDetailsOverview } from 'components/referrals/ReferralDetailsOverview';
import { ReferralAndOnboardingStatusIndicator } from 'components/shared/ReferralAndOnboardingStatusIndicator';
import { SpecialMemberHandlingName } from 'components/shared/SpecialMemberHandlingName';
import { Permissions } from 'services/constants';
import { type BackToDestination, useGoBack } from 'services/navigationHelpers';
import { useTransferredSegmentName } from 'services/referralMilestoneHelpers';
import { AppRoutes } from 'services/routes';
import { useScopeAssessorMemberDetails } from 'services/useScopeAssessorMemberDetails';
import { useSpecialMemberHandling } from 'services/useSpecialMemberHandling';
import { useAppSelector } from 'store';
import { useGetObjectiveStatusesQuery } from 'store/api/onboardingApi';
import { useGetOpportunityByTreatmentQuery } from 'store/api/opportunityCuratorApi';
import { useGetPerformanceEntriesForReferralQuery } from 'store/api/performanceApi';
import { useGetAllProductServicesQuery } from 'store/api/referenceApi';
import { useGetReferralDetailsQuery, useGetTreatmentDetailsQuery } from 'store/api/treatmentApi';

const currentLocation: BackToDestination = {
    text: 'Referral Details',
};

const backToDestinationDefault = {
    route: AppRoutes.treatments,
    text: 'Member Search',
};

export const ReferralDetailsPage = () => {
    const { referralId } = useParams() as { referralId: string };
    const navigate = useNavigate();
    const location = useLocation();
    const { showOpportunityDetailsInPayersApp } = useFlags<{
        showOpportunityDetailsInPayersApp: boolean;
    }>();

    const { payerId, permissions } = useAppSelector((state) => state.auth);
    const { data: referralDetails } = useGetReferralDetailsQuery({ payerId: payerId!, referralId });
    const { memberDetails } = useScopeAssessorMemberDetails(referralDetails);
    const specialMemberHandling = useSpecialMemberHandling(referralDetails, {
        routeCode: referralDetails?.routeCode,
        supplierNpi: referralDetails?.supplierNpi,
    });

    const { data: treatmentDetailsForIntakeMilestones } = useGetTreatmentDetailsQuery(
        {
            payerId: payerId!,
            treatmentId: referralDetails?.treatmentId ?? '',
        },
        { skip: !payerId || !referralDetails?.treatmentId },
    );
    const { data: performanceEntries } = useGetPerformanceEntriesForReferralQuery({
        payerId: payerId!,
        referralId,
    });
    const { data: productServiceData } = useGetAllProductServicesQuery();
    const { productServiceMap } = productServiceData ?? {};
    const transferredSegmentName = useTransferredSegmentName(referralId);

    const { data: opportunityDetails } = useGetOpportunityByTreatmentQuery(
        showOpportunityDetailsInPayersApp && referralDetails?.treatmentId
            ? {
                  treatmentId: referralDetails.treatmentId,
              }
            : skipToken,
    );
    const showOpportunityDetails = showOpportunityDetailsInPayersApp && !!opportunityDetails;

    const showFillProgress =
        referralDetails?.referralStatus &&
        (referralDetails.referralStatus === ReferralStatusIndicator.Statuses.PHARM ||
            referralDetails.referralStatus === ReferralStatusIndicator.Statuses.PENDREAUTH);

    const { data: objectiveStatuses } = useGetObjectiveStatusesQuery(
        showFillProgress ? { referralId } : skipToken,
    );

    const { backToDestination, goBack } = useGoBack(backToDestinationDefault);

    // navigate back to referral list if id is invalid
    useEffect(() => {
        if (referralDetails && !referralDetails.referralId) {
            goBack();
        }
    }, [referralDetails, goBack]);

    const [showClinicalPolicyDialog, setShowClinicalPolicyDialog] = useState(false);

    // Actions button menu items
    const menuItems =
        (referralDetails?.clinicalInfo?.clinicalPolicyAnswers ?? []).length > 0
            ? [
                  {
                      text: 'View Clinical Policy Responses',
                      onItemClick: () => setShowClinicalPolicyDialog(true),
                  },
              ]
            : [];

    if (referralDetails?.treatmentId) {
        menuItems.push({
            text: 'Go to Member Treatment',
            onItemClick: () =>
                navigate(AppRoutes.treatmentDetails(referralDetails.treatmentId), {
                    state: {
                        backToDestination: currentLocation,
                    },
                }),
        });
    }

    return (
        <>
            <ClinicalPolicyDialog
                isOpen={showClinicalPolicyDialog}
                onClose={() => setShowClinicalPolicyDialog(false)}
                referralDetails={referralDetails}
            />
            <FormHeaderAndTabbedContent
                rowOneContentHeader={
                    <>
                        <ActionButtonPageTitle
                            label={backToDestination.text}
                            title={currentLocation.text}
                            id="referral-details-back-button"
                            onClick={() => goBack()}
                        />
                        {menuItems.length > 0 && (
                            <div style={{ marginLeft: 'auto' }}>
                                <ButtonWithDropdownMenu
                                    label="Actions"
                                    menuItems={menuItems}
                                    intent={ButtonWithDropdownMenu.Intent.PRIMARY}
                                />
                            </div>
                        )}
                    </>
                }
                rowTwoContentHeader={
                    referralDetails && (
                        <>
                            <FormTitleLabelValuePair
                                label={
                                    <SpecialMemberHandlingName
                                        name={referralDetails?.memberName}
                                        tooltip={specialMemberHandling?.tooltip}
                                    />
                                }
                                value={referralDetails?.memberId}
                            />
                            <HeaderLabelValuePair
                                label="Product"
                                value={productServiceMap?.[referralDetails?.productServiceOid]}
                            />
                            {permissions.includes(Permissions.READ_REFERRAL_RATES) && (
                                <HeaderLabelValuePair
                                    label="Referral Rate"
                                    value={
                                        <ServicingRate
                                            servicingRate={referralDetails?.winningBidRate}
                                            referentPriceType={referralDetails?.referentPriceType}
                                        />
                                    }
                                />
                            )}
                            <HeaderLabelValuePair
                                label="Date Received"
                                value={formatDate(
                                    showOpportunityDetails
                                        ? opportunityDetails.receivedTime
                                        : referralDetails.receivedTime,
                                    DateFormats.DATE_TIME,
                                )}
                            />
                            <HeaderLabelValuePair
                                label="Referral Status"
                                value={
                                    showOpportunityDetails ? (
                                        <OpportunityStatusIndicator
                                            status={opportunityDetails?.referralStatus}
                                        />
                                    ) : (
                                        <ReferralAndOnboardingStatusIndicator
                                            status={referralDetails?.referralStatus}
                                            enrollmentStatus={memberDetails?.enrollmentStatus}
                                            referralId={referralDetails?.referralId}
                                        />
                                    )
                                }
                                showValueTooltip={false}
                            />
                            {showFillProgress && (
                                <HeaderLabelValuePair
                                    label="Fill Progress"
                                    value={
                                        <FillProgressIcon
                                            objectiveStatuses={objectiveStatuses ?? {}}
                                            showCompleteForOnTherapy
                                        />
                                    }
                                    showValueTooltip={false}
                                />
                            )}
                        </>
                    )
                }
                rowThreeContentHeader={
                    transferredSegmentName && (
                        <InlineMessage
                            intent={InlineMessage.Intent.INFO}
                            label={`Member's group coverage has changed, and has been transferred to the business segment ${
                                transferredSegmentName
                            }. Information on this page may be out of date.`}
                        />
                    )
                }
                tabs={[
                    {
                        title: 'Overview',
                        mainContent: (
                            <ReferralDetailsOverview
                                referralId={referralId}
                                showMemberDetails
                                currentLocation={currentLocation}
                                treatmentDetailsForIntakeMilestones={
                                    treatmentDetailsForIntakeMilestones
                                }
                            />
                        ),
                    },
                    {
                        title: 'Performance Progress',
                        rowOneContent: (performanceEntries ?? []).length > 0 && (
                            <PerformanceProgress performanceEntries={performanceEntries} />
                        ),
                    },
                ]}
                activeTab={location.hash.replace(/^#/, '').replace(/-/g, ' ')}
                onTabSelect={(tab) =>
                    navigate({
                        ...location,
                        hash: tab.replace(/\s/g, '-').toLocaleLowerCase(),
                    })
                }
            />
        </>
    );
};
